import 'maplibre-gl/dist/maplibre-gl.css';
import './style.css';

import { createMap } from './map/setup.js';
import { addCoreSources, addWaterSource } from './map/sources.js';
import { addCoreLayers, addWaterLayers } from './map/layers.js';
import { setupInteractions } from './map/interactions.js';
import { fetchBRPParcels, fetchNatura2000 } from './services/pdok-wfs.js';
import { fetchWaterdeel } from './services/pdok-bgt.js';
import { findOverlaps } from './analysis/intersect.js';
import { matchRegulations } from './analysis/rules-engine.js';
import { matchFutureChanges } from './config/future-changes.js';
import { showPlaceholder, showParcelInfo } from './ui/sidebar.js';
import { initLayerControl } from './ui/layer-control.js';
import { updateLoading, hideLoading } from './ui/loading.js';
import { OWN_PARCEL_IDS } from './config/user.js';
import turfBbox from '@turf/bbox';

let appData = {
  parcels: null,
  natura2000: null,
  water: null,
  bufferZones: null,
};

async function loadWithFallback(localPath, fetchFn, label) {
  try {
    const response = await fetch(localPath);
    if (!response.ok) throw new Error(`Lokaal bestand ${response.status}`);
    return await response.json();
  } catch (err) {
    console.warn(`Lokaal ${label} laden mislukt, live data ophalen:`, err.message);
    try {
      return await fetchFn();
    } catch (fetchErr) {
      console.error(`Live ${label} ook mislukt:`, fetchErr.message);
      return { type: 'FeatureCollection', features: [] };
    }
  }
}

function handleParcelClick(feature) {
  const layers = [
    { id: 'natura2000', data: appData.natura2000 },
    { id: 'buffer-zones', data: appData.bufferZones },
  ];

  if (appData.water) {
    layers.push({ id: 'water', data: appData.water });
  }

  const overlaps = findOverlaps(feature, layers);

  const props = feature.properties;
  const cropName = props.gewas || props.gewasnaam || props.GWS_GEWAS || 'Onbekend';
  const regulations = matchRegulations(overlaps);
  const futureChanges = matchFutureChanges(overlaps, cropName);
  showParcelInfo(feature, regulations, overlaps, futureChanges);
}

async function init() {
  const map = createMap();

  map.on('load', async () => {
    try {
      // Phase 1: Load parcels + natura2000 (fast — ~20 MB total)
      updateLoading('Percelen laden...', 20);
      const dataPath = import.meta.env.MODE === 'test' ? '/data/test' : '/data';

      const [parcels, natura2000, bufferZones] = await Promise.all([
        loadWithFallback(`${dataPath}/parcels.json`, fetchBRPParcels, 'BRP percelen'),
        loadWithFallback(`${dataPath}/natura2000.json`, fetchNatura2000, 'Natura 2000'),
        fetch(`${dataPath}/buffer-zones.json`).then((r) => r.json()).catch(() => ({ type: 'FeatureCollection', features: [] })),
      ]);

      updateLoading('Gegevens verwerken...', 60);

      // Assign stable numeric IDs for MapLibre feature state
      parcels.features.forEach((f, i) => {
        if (!f.properties.id) {
          f.properties.id = f.id || `parcel-${i}`;
        }
        if (typeof f.properties.gewas === 'string') {
          f.properties.gewas = f.properties.gewas.trim();
        }
        f.properties._isOwn = OWN_PARCEL_IDS.includes(f.properties.id);
        f.properties._numericId = i;
        f.id = i;
      });

      appData.parcels = parcels;
      appData.natura2000 = natura2000;
      appData.bufferZones = bufferZones;

      updateLoading('Kaart opbouwen...', 80);

      // Add core sources + layers and make map interactive
      addCoreSources(map, { parcels, natura2000, bufferZones });
      addCoreLayers(map);
      initLayerControl(map, () => ensureWaterLoaded(map));

      setupInteractions(map, {
        onParcelClick: (feature) => handleParcelClick(feature),
        onDeselect: () => showPlaceholder(),
      });

      // "Toon mijn percelen" button
      const ownFeatures = parcels.features.filter((f) => f.properties._isOwn);
      if (ownFeatures.length > 0) {
        const btn = document.createElement('button');
        btn.className = 'own-parcels-btn';
        btn.textContent = 'Toon mijn percelen';
        btn.addEventListener('click', () => {
          const fc = { type: 'FeatureCollection', features: ownFeatures };
          const bbox = turfBbox(fc);
          map.fitBounds(bbox, { padding: 80, pitch: 45 });
        });
        document.getElementById('map-container').appendChild(btn);
      }

      showPlaceholder();
      updateLoading('Gereed!', 100);
      setTimeout(() => hideLoading(), 400);

      console.log(
        `Geladen: ${parcels.features.length} percelen, ` +
        `${natura2000.features.length} Natura 2000-gebieden, ` +
        `${bufferZones.features.length} bufferzones`
      );
    } catch (err) {
      console.error('Fout bij laden:', err);
      updateLoading(`Fout bij laden: ${err.message}`, 0);
    }
  });
}

let waterLoadPromise = null;

function ensureWaterLoaded(map) {
  if (appData.water) return Promise.resolve();
  if (waterLoadPromise) return waterLoadPromise;

  const dataPath = import.meta.env.MODE === 'test' ? '/data/test' : '/data';
  waterLoadPromise = loadWaterData(map, dataPath);
  return waterLoadPromise;
}

async function loadWaterData(map, dataPath) {
  try {
    const water = await loadWithFallback(
      `${dataPath}/waterdeel.json`, fetchWaterdeel, 'waterdelen'
    );

    appData.water = water;

    addWaterSource(map, water);
    addWaterLayers(map);

    console.log(`Lazy geladen: ${water.features.length} waterdelen`);
  } catch (err) {
    console.warn('Waterdata laden mislukt:', err.message);
  }
}

init();
